
import { supabase } from '@/lib/supabase';

export interface ConsistencyIssue {
  type: 'orphaned_profile' | 'missing_referral' | 'mismatched_assignment' | 'missing_limits';
  userId: string;
  userName?: string;
  userEmail?: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  autoFixable: boolean;
  recommendedAction: string;
  data?: any;
}

export interface ConsistencyReport {
  totalUsers: number;
  consistentUsers: number;
  inconsistentUsers: number;
  issues: ConsistencyIssue[];
  summary: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    autoFixable: number;
  };
}

class AffiliateDataConsistencyService {
  private async isAdmin(): Promise<boolean> {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return false;
    
    return user.user.app_metadata?.role === 'admin' || user.user.app_metadata?.role === 'superadmin';
  }

  /**
   * Comprehensive data consistency check
   */
  async runConsistencyCheck(): Promise<ConsistencyReport> {
    try {
      if (!await this.isAdmin()) {
        throw new Error('Unauthorized: Admin access required');
      }

      console.log('🔍 Running comprehensive affiliate data consistency check...');

      const issues: ConsistencyIssue[] = [];

      // Get all users (excluding affiliates themselves)
      const { data: allUsers, error: usersError } = await supabase
        .from('user_profiles')
        .select(`
          user_id,
          display_name,
          display_email,
          referred_by_affiliate,
          created_at
        `);

      if (usersError) {
        throw usersError;
      }

      // Get all referrals
      const { data: referrals, error: referralsError } = await supabase
        .from('referrals')
        .select('referred_user_id, referrer_id, referral_code, created_at');

      if (referralsError) {
        throw referralsError;
      }

      // Get affiliate user limits
      const { data: userLimits, error: limitsError } = await supabase
        .from('affiliate_user_limits')
        .select('user_id, affiliate_id, feature_type');

      if (limitsError) {
        throw limitsError;
      }

      // Get affiliates for exclusion
      const { data: affiliates, error: affiliatesError } = await supabase
        .from('affiliates')
        .select('id');

      const affiliateIds = new Set(affiliates?.map(a => a.id) || []);
      const referralMap = new Map(referrals?.map(r => [r.referred_user_id, r]) || []);
      const limitsMap = new Map(userLimits?.map(l => [`${l.user_id}_${l.affiliate_id}`, l]) || []);

      let consistentUsers = 0;

      // Check each user for consistency issues
      for (const user of allUsers || []) {
        // Skip affiliate users
        if (affiliateIds.has(user.user_id)) {
          continue;
        }

        const hasProfileAssignment = !!user.referred_by_affiliate;
        const hasReferralRecord = referralMap.has(user.user_id);
        const referralRecord = referralMap.get(user.user_id);
        const hasLimits = limitsMap.has(`${user.user_id}_${user.referred_by_affiliate}`);

        let userHasIssues = false;

        // Issue 1: Profile has assignment but no referral record
        if (hasProfileAssignment && !hasReferralRecord) {
          issues.push({
            type: 'missing_referral',
            userId: user.user_id,
            userName: user.display_name || 'Unknown',
            userEmail: user.display_email || 'Unknown',
            description: 'User profile shows affiliate assignment but no referral record exists',
            severity: 'high',
            autoFixable: true,
            recommendedAction: 'Create referral record or clear profile assignment',
            data: { affiliateId: user.referred_by_affiliate }
          });
          userHasIssues = true;
        }

        // Issue 2: Referral record exists but profile not assigned
        if (!hasProfileAssignment && hasReferralRecord) {
          issues.push({
            type: 'orphaned_profile',
            userId: user.user_id,
            userName: user.display_name || 'Unknown',
            userEmail: user.display_email || 'Unknown',
            description: 'Referral record exists but user profile not assigned to affiliate',
            severity: 'critical',
            autoFixable: true,
            recommendedAction: `Update profile with affiliate: ${referralRecord?.referrer_id}`,
            data: { affiliateId: referralRecord?.referrer_id }
          });
          userHasIssues = true;
        }

        // Issue 3: Mismatched assignments
        if (hasProfileAssignment && hasReferralRecord && user.referred_by_affiliate !== referralRecord?.referrer_id) {
          issues.push({
            type: 'mismatched_assignment',
            userId: user.user_id,
            userName: user.display_name || 'Unknown',
            userEmail: user.display_email || 'Unknown',
            description: 'Profile affiliate assignment does not match referral record',
            severity: 'critical',
            autoFixable: true,
            recommendedAction: `Sync assignments - Profile: ${user.referred_by_affiliate}, Referral: ${referralRecord?.referrer_id}`,
            data: { 
              profileAffiliate: user.referred_by_affiliate,
              referralAffiliate: referralRecord?.referrer_id
            }
          });
          userHasIssues = true;
        }

        // Issue 4: Missing user limits for assigned users
        if (hasProfileAssignment && !hasLimits) {
          issues.push({
            type: 'missing_limits',
            userId: user.user_id,
            userName: user.display_name || 'Unknown',
            userEmail: user.display_email || 'Unknown',
            description: 'User assigned to affiliate but missing usage limits',
            severity: 'medium',
            autoFixable: true,
            recommendedAction: 'Create default usage limits for user',
            data: { affiliateId: user.referred_by_affiliate }
          });
          userHasIssues = true;
        }

        if (!userHasIssues) {
          consistentUsers++;
        }
      }

      // Generate summary
      const summary = issues.reduce((acc, issue) => {
        acc[issue.severity]++;
        if (issue.autoFixable) acc.autoFixable++;
        return acc;
      }, { critical: 0, high: 0, medium: 0, low: 0, autoFixable: 0 });

      const report: ConsistencyReport = {
        totalUsers: (allUsers?.length || 0) - affiliateIds.size,
        consistentUsers,
        inconsistentUsers: (allUsers?.length || 0) - affiliateIds.size - consistentUsers,
        issues,
        summary
      };

      console.log('📊 Consistency check completed:', {
        totalUsers: report.totalUsers,
        consistentUsers: report.consistentUsers,
        inconsistentUsers: report.inconsistentUsers,
        totalIssues: issues.length,
        autoFixable: summary.autoFixable
      });

      return report;

    } catch (error) {
      console.error('❌ Consistency check failed:', error);
      throw error;
    }
  }

  /**
   * Auto-fix data consistency issues
   */
  async autoFixIssues(issues: ConsistencyIssue[]): Promise<{
    fixed: number;
    failed: number;
    details: string[];
  }> {
    try {
      if (!await this.isAdmin()) {
        throw new Error('Unauthorized: Admin access required');
      }

      console.log('🔧 Starting auto-fix for', issues.length, 'issues...');

      let fixed = 0;
      let failed = 0;
      const details: string[] = [];

      for (const issue of issues.filter(i => i.autoFixable)) {
        try {
          switch (issue.type) {
            case 'orphaned_profile':
              // Update user profile to match referral record
              const { error: profileUpdateError } = await supabase
                .from('user_profiles')
                .update({ 
                  referred_by_affiliate: issue.data.affiliateId,
                  updated_at: new Date().toISOString()
                })
                .eq('user_id', issue.userId);

              if (profileUpdateError) {
                failed++;
                details.push(`Failed to fix orphaned profile for ${issue.userName}: ${profileUpdateError.message}`);
              } else {
                fixed++;
                details.push(`Fixed orphaned profile for ${issue.userName}`);
              }
              break;

            case 'missing_referral':
              // Create referral record for assigned user
              const { error: referralCreateError } = await supabase
                .from('referrals')
                .insert({
                  referrer_id: issue.data.affiliateId,
                  referred_user_id: issue.userId,
                  referral_code: `BACKFILL_${Date.now()}`,
                  status: 'registered',
                  commission_amount: 0,
                  is_paid: false
                });

              if (referralCreateError) {
                failed++;
                details.push(`Failed to create referral record for ${issue.userName}: ${referralCreateError.message}`);
              } else {
                fixed++;
                details.push(`Created referral record for ${issue.userName}`);
              }
              break;

            case 'mismatched_assignment':
              // Use referral record as source of truth
              const { error: syncError } = await supabase
                .from('user_profiles')
                .update({ 
                  referred_by_affiliate: issue.data.referralAffiliate,
                  updated_at: new Date().toISOString()
                })
                .eq('user_id', issue.userId);

              if (syncError) {
                failed++;
                details.push(`Failed to sync assignment for ${issue.userName}: ${syncError.message}`);
              } else {
                fixed++;
                details.push(`Synced assignment for ${issue.userName}`);
              }
              break;

            case 'missing_limits':
              // Create default limits
              const { data: affiliateDefaults, error: defaultsError } = await supabase
                .rpc('get_affiliate_effective_defaults', { 
                  affiliate_uuid: issue.data.affiliateId 
                });

              if (!defaultsError && affiliateDefaults?.[0]) {
                const { error: limitsError } = await supabase
                  .from('affiliate_user_limits')
                  .insert({
                    affiliate_id: issue.data.affiliateId,
                    user_id: issue.userId,
                    feature_type: 'market_analysis',
                    daily_limit: affiliateDefaults[0].daily_limit,
                    monthly_limit: affiliateDefaults[0].monthly_limit,
                    is_default: true
                  });

                if (limitsError) {
                  failed++;
                  details.push(`Failed to create limits for ${issue.userName}: ${limitsError.message}`);
                } else {
                  fixed++;
                  details.push(`Created default limits for ${issue.userName}`);
                }
              } else {
                failed++;
                details.push(`Failed to get defaults for ${issue.userName}`);
              }
              break;

            default:
              failed++;
              details.push(`Unknown issue type: ${issue.type} for ${issue.userName}`);
          }
        } catch (error) {
          failed++;
          details.push(`Exception fixing ${issue.userName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      console.log(`✅ Auto-fix completed: ${fixed} fixed, ${failed} failed`);
      return { fixed, failed, details };

    } catch (error) {
      console.error('❌ Auto-fix failed:', error);
      throw error;
    }
  }
}

export const affiliateDataConsistencyService = new AffiliateDataConsistencyService();
